import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminStatementList } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface StatementsState {
  status: 'loading' | 'ready' | 'error';
  data: AdminStatementList | null;
  error: ApiError | null;
  reload: () => void;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/**
 * This tenant's subscriber statements (`GET …/tenants/{slug}/statements?status=&period=`, H §6.2).
 * `period` is a month (`YYYY-MM`), which tmm-api prefix-matches on the statement's `periodStart`
 * (TMMRSL-0073). The reseller's own payout statements are reseller-wide (`/payouts`, TMMRSL-0031),
 * never here. Refetches when a filter changes; a late answer for an old filter is dropped.
 */
export function useStatements(
  statusFilter: string | undefined,
  period?: string | undefined,
): StatementsState {
  const api = useTenantApi();
  const [status, setStatus] = useState<StatementsState['status']>('loading');
  const [data, setData] = useState<AdminStatementList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const seq = useRef(0);

  const reload = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    setError(null);
    const params = {
      ...(statusFilter !== undefined ? { status: statusFilter } : {}),
      ...(period !== undefined ? { period } : {}),
    };
    api.listStatements(Object.keys(params).length > 0 ? params : undefined).then(
      (next) => {
        if (seq.current !== mine) return;
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        if (seq.current !== mine) return;
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api, statusFilter, period]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { status, data, error, reload };
}
